"use client";
import React from "react";
import { Check, DollarSign, MapPin, User } from "lucide-react";
import NFTPrice from "./nft-price";
import LazyNFTMedia from "./lazyLoadImage";

type NFTAttribute = {
	trait_type: string;
	value: string;
};

type NFTMetadata = {
	name: string;
	image: string;
	attributes?: NFTAttribute[];
	[key: string]: any;
};

type PinLocation = {
	formattedAddress: string;
	[key: string]: any;
};

type SearchResultItemProps = {
	pin: {
		_id: string;
		nftMetaData: NFTMetadata[];
		pinLocation: PinLocation;
		[key: string]: any;
	};
};

const SearchResultItem: React.FC<SearchResultItemProps> = ({ pin }) => {
	return (
		<div className="w-full p-4 bg-mgt-black/70 rounded-xl hover:bg-mgt-black/40 transition-colors duration-200 my-2">
			<div className="flex items-start space-x-3">
				<div className="relative w-16 h-16 flex-shrink-0">
					<LazyNFTMedia
						src={pin.nftMetaData[0].image}
						alt={pin.nftMetaData[0].name || "NFT Image"}
						isSelected={false}
						isCompressed={false}
						width={64}
						height={64}
						quality={85}
						loading="lazy"
						className="rounded-lg"
					/>
				</div>
				<div className="flex-1 min-w-0">
					<div className="flex items-start justify-between">
						<div className="min-w-0 flex-1">
							<h3 className="text-white font-medium truncate">
								{pin.nftMetaData[0].name}
							</h3>
							<div className="flex items-center space-x-2 mt-1">
								<MapPin className="text-red-400 size-4 flex-shrink-0" />
								<p className="text-sm text-gray-300 truncate">
									{pin.pinLocation.city}, {pin.pinLocation.country}
								</p>
							</div>
							<div className="flex items-center space-x-2 mt-1">
								<DollarSign className="text-green-400 size-4 flex-shrink-0" />
								<NFTPrice
									priceInUSD={pin.stashDetails?.pricePerNft}
									textClassName="text-sm text-gray-300"
								/>
							</div>
						</div>
						<div className="flex items-center space-x-2 flex-shrink-0">
							{pin.userDetails?.image ? (
								<div className="relative h-8 w-8">
									<LazyNFTMedia
										src={pin.userDetails.image}
										alt={pin.userDetails.userName || "User"}
										isSelected={false}
										width={32}
										height={32}
										quality={75}
										loading="lazy"
										className="rounded-full"
									/>
								</div>
							) : (
								<div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center">
									<User className="size-4 text-gray-600" />
								</div>
							)}
							<div className="flex items-center space-x-1">
								<span className="text-gray-600 text-sm truncate">
									{pin.userDetails?.userName || "Anonymous"}
								</span>
								{pin.userDetails?.role === "admin" && (
									<div className="bg-mgt-green rounded-full p-0.5 flex items-center justify-center">
										<Check className="size-3 text-mgt-black" />
									</div>
								)}
							</div>
						</div>
					</div>

					<div className="mt-2 flex flex-wrap gap-1.5">
						{pin.nftMetaData[0].attributes?.map(
							(attr: NFTAttribute, index: number) => (
								<span
									key={index}
									className={`
                  inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-mgt-green text-white`}
								>
									{attr.trait_type}: {attr.value}
								</span>
							)
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default SearchResultItem;
